import { inject, ObjectGraph } from '@jet/environment/dependencies';
import {
    IntentDispatcher,
    type Dispatchable,
    type IIntentDispatcher,
    type Intent,
    type IntentController,
    type IntentReturnType,
    isBaseActionProvider,
} from '@jet/environment/dispatching';
import {
    FetchTimingMetricsBuilder,
    fetchTimingMetricsBuilderType,
} from '@jet/environment/metrics';
import type { ActionModel } from '@jet/environment/types/models';
import type { Optional } from '@jet/environment/types/optional';
import { ObjectGraphType } from '../dependencies/object-graph-type';
import { IDProvider, IDProviderType } from '../utilities';
import type { ActionProvider, ActionProviderOptions } from './action-provider';

export class Dispatcher implements IIntentDispatcher {
    private readonly dispatcher: IntentDispatcher;
    private readonly defaultActionProvider: ActionProvider<Intent<unknown>>;

    constructor(defaultActionProvider: ActionProvider<Intent<unknown>>) {
        this.dispatcher = new IntentDispatcher();
        this.defaultActionProvider = defaultActionProvider;
    }

    // MARK: - IBaseIntentDispatcher

    register<I extends Intent<unknown>>(controller: IntentController<I>): void {
        this.dispatcher.register(controller);
    }

    async dispatch<I extends Intent<unknown>>(
        intent: I,
        objectGraph: ObjectGraph,
    ): Promise<IntentReturnType<I>> {
        // <https://github.pie.apple.com/music/music-ui-js/pull/67#discussion_r4761652>
        const localization = inject(ObjectGraphType.localization, objectGraph);
        await localization.loadStrings();

        // The "global" logger
        const logger = inject(ObjectGraphType.logger, objectGraph);
        if (logger.shouldQueueMessages) {
            // rdar://81186401 (JS console messages dropped when called early in
            // JS bootstrap process). Unfortunately we can't guarantee that
            // console logging is enabled at this point, but this seems like the
            // best place to try flushing the messages.
            logger.flushMessages();
            logger.shouldQueueMessages = false;
        }

        const intentObjectGraph = this.objectGraphWithIntentDependencies(
            objectGraph,
            intent.$kind,
        );

        const result = await this.dispatcher.dispatch(
            intent,
            intentObjectGraph,
        );
        return result;
    }

    controller<I extends Intent<unknown>>(
        intent: I,
    ): Optional<IntentController<I>> {
        return this.dispatcher.controller(intent);
    }

    get registeredControllers(): Dispatchable<Intent<unknown>>[] {
        return this.dispatcher.registeredControllers;
    }

    // MARK: - Actions

    actionFor(
        intent: Intent<unknown>,
        objectGraph: ObjectGraph,
        options: ActionProviderOptions,
    ): Optional<ActionModel> {
        const resolvedController = this.dispatcher.controller(intent);
        if (isBaseActionProvider(resolvedController)) {
            return resolvedController.actionFor(intent, objectGraph, options);
        }
        return this.defaultActionProvider.actionFor(
            intent,
            objectGraph,
            options,
        );
    }

    // MARK: - Private

    private objectGraphWithIntentDependencies(
        objectGraph: ObjectGraph,
        label: string,
    ): ObjectGraph {
        const loggerFactory = inject(
            ObjectGraphType.loggerFactory,
            objectGraph,
        );
        const objectGraphWithContextualLogger = objectGraph.adding(
            ObjectGraphType.logger,
            loggerFactory.build(label),
        );

        // The `FetchTimingMetricsBuilder` is used for instrumenting page render
        // metrics. Creating here allows all intent dispatches to have one
        // available from the object graph.
        const fetchTimingMetricsBuilder = new FetchTimingMetricsBuilder();

        return objectGraphWithContextualLogger
            .adding(fetchTimingMetricsBuilderType, fetchTimingMetricsBuilder)
            .adding(
                IDProviderType,
                new IDProvider(objectGraphWithContextualLogger),
            );
    }
}
